import { z } from 'zod';
import type { ShapeManifest } from '../../../lib/diagram/shape-manifest.types';
import i18n from '../../../i18n/config';

const stickyNoteProps = z.object({
  text: z.string().default('Note'),
  fontSize: z.number().min(1).default(14),
  fontFamily: z.string().default('Noto Sans'),
  fontWeight: z.union([z.string(), z.number()]).optional(),
  fontStyle: z.string().optional(),
  textAlign: z.enum(['left', 'center', 'right']).optional(),
  verticalAlign: z.enum(['top', 'middle', 'bottom']).optional(),
  textColor: z.string().optional(),
  textDecoration: z.string().optional(),
  fillColor: z.string().optional(),
  strokeColor: z.string().optional(),
  strokeWidth: z.number().optional(),
  lineHeight: z.number().min(0.5).max(3).optional(),
  attrs: z.record(z.string(), z.any()).optional(),
});

export type StickyNoteProps = z.infer<typeof stickyNoteProps>;

const DEFAULT_FONT_SIZE = 14;
const DEFAULT_TEXT_COLOR = '#0F172A';
const DEFAULT_FILL = '#FEF3C7';
const DEFAULT_STROKE = '#FCD34D';
const DEFAULT_LINE_HEIGHT = 1.2;
const PADDING = 10;
const CORNER_RADIUS = 2;
const SHADOW_COLOR = 'rgba(0, 0, 0, 0.3)';
const SHADOW_BLUR = 8;
const SHADOW_OFFSET_Y = 4;
const SHADOW_OPACITY = 0.6;

export const CommonStickyNoteManifest: ShapeManifest<StickyNoteProps> = {
  id: 'sticky-note',
  version: '1.0.0',
  category: 'annotation',
  propsSchema: stickyNoteProps,
  defaultProps: () => ({
    text: i18n.t('genogram:shapes.stickyNoteDefault', { defaultValue: 'Note' }) as string,
    fontSize: DEFAULT_FONT_SIZE,
    fontFamily: 'Noto Sans',
  }),
  defaultDimensions: { width: 150, height: 150 },
  capabilities: {
    selectable: true,
    transformable: true,
    textEditable: true,
  },
  renderers: {
    svg: ({ viewBox, viewModel, props }) => {
      const boundsWidth = Math.max(1, Number(viewBox.w) || 150);
      const boundsHeight = Math.max(1, Number(viewBox.h) || 150);

      const style = (viewModel?.style ?? {}) as Record<string, unknown>;
      const attrs = (props.attrs ?? {}) as Record<string, unknown>;

      const fontSize =
        coalesceNumber(props.fontSize, attrs.fontSize, style.fontSize, DEFAULT_FONT_SIZE) ?? DEFAULT_FONT_SIZE;
      const fontFamily =
        coalesceString(
          props.fontFamily,
          attrs.fontFamily,
          style.fontFamily,
          'Noto Sans, Inter, Arial, sans-serif',
        ) ?? 'Noto Sans, Inter, Arial, sans-serif';
      const fontWeight = normaliseFontWeight(coalesce(props.fontWeight, attrs.fontWeight, style.fontWeight));
      const fontStyle = coalesceString(props.fontStyle, attrs.fontStyle, style.fontStyle);
      const textDecoration = coalesceString(props.textDecoration, attrs.textDecoration, style.textDecoration);
      const textAlign = (coalesceString(props.textAlign, attrs.textAlign, style.textAlign) ?? 'center') as
        | 'left'
        | 'center'
        | 'right';
      const verticalAlign = (coalesceString(props.verticalAlign, attrs.verticalAlign, style.verticalAlign) ?? 'middle') as
        | 'top'
        | 'middle'
        | 'bottom';
      const textColor =
        coalesceString(props.textColor, attrs.textColor, style.textColor, DEFAULT_TEXT_COLOR) ?? DEFAULT_TEXT_COLOR;
      const fillColor =
        coalesceString(
          props.fillColor,
          style.fillColor,
          style.fill,
          attrs.fillColor,
          attrs.fill,
          DEFAULT_FILL,
        ) ?? DEFAULT_FILL;
      const strokeColor =
        coalesceString(
          props.strokeColor,
          style.strokeColor,
          style.stroke,
          attrs.strokeColor,
          attrs.stroke,
          DEFAULT_STROKE,
        ) ?? DEFAULT_STROKE;
      const strokeWidth =
        coalesceNumber(props.strokeWidth, style.strokeWidth, attrs.strokeWidth, 0.8) ?? 0.8;
      const lineHeightMultiplier =
        coalesceNumber(props.lineHeight, attrs.lineHeight, style.lineHeight, DEFAULT_LINE_HEIGHT) ??
        DEFAULT_LINE_HEIGHT;

      const rawText = coalesceString(props.text, attrs.text) ?? '';
      const normalizedText = normalizeLineEndings(rawText);

      const measure = createTextMeasurer(fontFamily, fontSize, fontWeight, fontStyle);
      const contentWidth = Math.max(0, boundsWidth - PADDING * 2);
      const lines = layoutText(normalizedText, contentWidth, measure);
      const lineHeight = Math.max(10, fontSize * lineHeightMultiplier);
      const textBlockHeight = Math.max(lineHeight, lines.length * lineHeight);

      let offsetY = PADDING;
      if (verticalAlign === 'middle') {
        offsetY = Math.max(PADDING, (boundsHeight - textBlockHeight) / 2);
      } else if (verticalAlign === 'bottom') {
        offsetY = Math.max(PADDING, boundsHeight - textBlockHeight - PADDING);
      }

      const anchor = resolveAnchor(textAlign, boundsWidth);

      const background = `<rect x="0" y="0" width="${formatNumber(boundsWidth)}" height="${formatNumber(boundsHeight)}" rx="${CORNER_RADIUS}" ry="${CORNER_RADIUS}" fill="${escapeAttr(fillColor)}" stroke="${escapeAttr(strokeColor)}" stroke-width="${formatNumber(strokeWidth)}" filter="url(#sticky-note-shadow)" />`;

      const textAttrs: string[] = [
        `x="${formatNumber(anchor.x)}"`,
        `y="${formatNumber(offsetY)}"`,
        `fill="${escapeAttr(textColor)}"`,
        `font-size="${formatNumber(fontSize)}"`,
        `font-family="${escapeAttr(fontFamily)}"`,
        `dominant-baseline="hanging"`,
        `text-anchor="${anchor.textAnchor}"`,
      ];
      if (textDecoration) {
        textAttrs.push(`text-decoration="${escapeAttr(textDecoration)}"`);
      }
      if (fontWeight) {
        textAttrs.push(`font-weight="${escapeAttr(fontWeight)}"`);
      }
      if (fontStyle && fontStyle.includes('italic')) {
        textAttrs.push('font-style="italic"');
      }

      const textContent = lines
        .map((line, index) => {
          const safeLine = line.length ? escapeText(line) : '&#8203;';
          const dy = index === 0 ? '0' : formatNumber(lineHeight);
          return `<tspan x="${formatNumber(anchor.x)}" dy="${dy}">${safeLine}</tspan>`;
        })
        .join('');

      const shadowDefinition = `<defs><filter id="sticky-note-shadow" x="-20%" y="-20%" width="140%" height="140%"><feDropShadow dx="0" dy="${formatNumber(SHADOW_OFFSET_Y)}" stdDeviation="${formatNumber(SHADOW_BLUR / 2)}" flood-color="${escapeAttr(
        SHADOW_COLOR,
      )}" flood-opacity="${formatNumber(SHADOW_OPACITY)}" /></filter></defs>`;

      const textElement = `<text ${textAttrs.join(' ')}>${textContent}</text>`;

      return `<g>${shadowDefinition}${background}${textElement}</g>`;
    },
  },
};

function coalesce<T>(...values: Array<T | null | undefined>): T | undefined {
  for (const value of values) {
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
}

function coalesceString(...values: Array<unknown>): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) return value;
  }
  return undefined;
}

function coalesceNumber(...values: Array<unknown>): number | undefined {
  for (const value of values) {
    const num = Number(value);
    if (Number.isFinite(num)) return num;
  }
  return undefined;
}

function normaliseFontWeight(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const clamped = Math.min(900, Math.max(100, Math.round(value / 100) * 100));
    return String(clamped);
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'bold') return '700';
    if (normalized === 'normal') return '400';
    if (/^[1-9]00$/.test(normalized)) return normalized;
    return value;
  }
  return undefined;
}

function normalizeLineEndings(text: string): string {
  return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

function createTextMeasurer(
  _fontFamily: string,
  fontSize: number,
  fontWeight?: string,
  fontStyle?: string,
): (text: string) => number {
  const isBold = fontWeight === '700' || fontWeight === 'bold';
  const isItalic = fontStyle?.includes('italic');
  const baseWidth = fontSize * 0.55;
  const weightFactor = isBold ? 1.08 : 1;
  const italicFactor = isItalic ? 1.03 : 1;
  return (text: string) => text.length * baseWidth * weightFactor * italicFactor;
}

function layoutText(text: string, maxWidth: number, measure: (text: string) => number): string[] {
  const lines: string[] = [];
  const paragraphs = text.split('\n');

  paragraphs.forEach((para, index) => {
    const wrapped = wrapParagraph(para, maxWidth, measure);
    if (wrapped.length === 0) {
      lines.push('');
    } else {
      lines.push(...wrapped);
    }
    if (index < paragraphs.length - 1 && para.length === 0) {
      lines.push('');
    }
  });

  return lines.length ? lines : [''];
}

function wrapParagraph(paragraph: string, maxWidth: number, measure: (text: string) => number): string[] {
  if (paragraph.length === 0) return [''];

  const tokens = paragraph.split(/(\s+)/);
  const lines: string[] = [];
  let current = '';

  const pushCurrent = () => {
    if (current.length > 0) {
      lines.push(current);
      current = '';
    }
  };

  for (const token of tokens) {
    if (!token) continue;
    const next = current + token;
    if (measure(next) <= maxWidth || current === '') {
      current = next;
      continue;
    }

    pushCurrent();
    current = token.trimStart();

    if (current && measure(current) > maxWidth) {
      let buffer = '';
      for (const ch of current) {
        const tentative = buffer + ch;
        if (measure(tentative) <= maxWidth || buffer.length === 0) {
          buffer = tentative;
        } else {
          lines.push(buffer);
          buffer = ch;
        }
      }
      current = buffer;
    }
  }

  if (current.length > 0) {
    lines.push(current);
  }

  return lines.length ? lines : [''];
}

function resolveAnchor(alignment: 'left' | 'center' | 'right', width: number): { x: number; textAnchor: string } {
  if (alignment === 'center') {
    return { x: width / 2, textAnchor: 'middle' };
  }
  if (alignment === 'right') {
    return { x: width - PADDING, textAnchor: 'end' };
  }
  return { x: PADDING, textAnchor: 'start' };
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/'/g, '&apos;');
}

function escapeText(value: string): string {
  return escapeAttr(value);
}

function formatNumber(value: number): string {
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(2).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
}
